import type { FolderListItem } from '../../../../types/ai-classify'
import type { ItemContentRect } from '../BoxSelect/boxSelectGeometry'

export const OTHER_DIMENSION_GROUP_KEY = '__other__'
export const OTHER_DIMENSION_GROUP_TITLE = '其他'
export const DIMENSION_GROUP_HEADER_HEIGHT = 44

export interface FolderDimensionGroup {
  key: string
  dimensionGroupId?: string
  title: string
  folders: FolderListItem[]
}

export interface FolderDimensionGroupOptions {
  /** 无维度文件夹分组标题；根目录默认“其他”，详情页纯无维度时可改为“子文件夹”。 */
  otherGroupTitle?: string
}

export type GroupedGridRow =
  | { kind: 'header'; group: FolderDimensionGroup }
  | { kind: 'folders'; groupKey: string; folders: FolderListItem[] }

/**
 * 取文件夹所属的选择分组 key。
 *
 * dimensionGroupID 是服务端下发的维度组身份；缺失 / 0 的普通文件夹归入“其他”。
 * 选择锁定、宫格分组和列表分组必须共用这一处判断，避免出现“展示在其他、选择时
 * 却被当成另一个组”的不一致。
 */
export function getFolderDimensionGroupKey(folder: FolderListItem): string {
  const rawId = folder.dimensionGroupId
  return rawId && rawId !== '0'
    ? `dimension:${rawId}`
    : OTHER_DIMENSION_GROUP_KEY
}

/** 是否存在真实分类维度；“其他”、待归档虚拟项和照片都不算分类维度。 */
export function hasFolderDimensionGroup(folders: FolderListItem[]): boolean {
  return folders.some(
    (folder) =>
      !folder.virtual &&
      !!folder.dimensionGroupId &&
      folder.dimensionGroupId !== '0'
  )
}

/** 当前选择锁定到的首个分组；selectedIds 顺序就是用户的选择顺序。 */
export function getLockedFolderGroupKey(
  folders: FolderListItem[],
  selectedIds: readonly string[]
): string | undefined {
  if (selectedIds.length === 0) return undefined
  const byId = new Map(folders.map((folder) => [folder.folderId, folder]))
  for (const id of selectedIds) {
    const folder = byId.get(id)
    if (folder && !folder.virtual) return getFolderDimensionGroupKey(folder)
  }
  return undefined
}

/** 根目录多选规则：一次只能选择一个分组，“其他”也视为独立分组。 */
export function isFolderGroupSelectable(
  groupKey: string,
  lockedGroupKey?: string
): boolean {
  return !lockedGroupKey || groupKey === lockedGroupKey
}

/** 把框选或历史残留选择收敛到首个选中的单一分组。 */
export function constrainFolderSelection(
  folders: FolderListItem[],
  selectedIds: readonly string[],
  lockedGroupKey = getLockedFolderGroupKey(folders, selectedIds)
): string[] {
  if (!lockedGroupKey) return []
  const byId = new Map(folders.map((folder) => [folder.folderId, folder]))
  return selectedIds.filter((id) => {
    const folder = byId.get(id)
    return (
      !!folder &&
      !folder.virtual &&
      getFolderDimensionGroupKey(folder) === lockedGroupKey
    )
  })
}

/**
 * 把同一父目录下的文件夹按服务端 dimensionGroupID 分组。
 * 没有有效维度身份的普通、移动后解绑和虚拟文件夹统一放在末尾“其他”。
 */
export function groupFoldersByDimension(
  folders: FolderListItem[],
  options: FolderDimensionGroupOptions = {}
): FolderDimensionGroup[] {
  const groups = new Map<string, FolderDimensionGroup>()
  let other: FolderDimensionGroup | undefined

  for (const folder of folders) {
    const groupKey = getFolderDimensionGroupKey(folder)
    if (groupKey === OTHER_DIMENSION_GROUP_KEY) {
      if (!other) {
        other = {
          key: OTHER_DIMENSION_GROUP_KEY,
          title: options.otherGroupTitle || OTHER_DIMENSION_GROUP_TITLE,
          folders: []
        }
      }
      other.folders.push(folder)
      continue
    }

    let group = groups.get(groupKey)
    if (!group) {
      group = {
        key: groupKey,
        dimensionGroupId: folder.dimensionGroupId,
        title: folder.dimensionDisplayName || '智能分类',
        folders: []
      }
      groups.set(groupKey, group)
    } else if (group.title === '智能分类' && folder.dimensionDisplayName) {
      group.title = folder.dimensionDisplayName
    }
    group.folders.push(folder)
  }

  const result = Array.from(groups.values())
  if (other && other.folders.length > 0) result.push(other)
  return result
}

/** 把分组拆成“标题行 + 若干文件夹行”，供虚拟宫格按可变行高渲染。 */
export function buildGroupedGridRows(
  groups: FolderDimensionGroup[],
  columnCount: number,
  showGroupHeaders = true
): GroupedGridRow[] {
  const cols = Math.max(1, columnCount)
  const rows: GroupedGridRow[] = []
  for (const group of groups) {
    if (showGroupHeaders) rows.push({ kind: 'header', group })
    for (let i = 0; i < group.folders.length; i += cols) {
      rows.push({
        kind: 'folders',
        groupKey: group.key,
        folders: group.folders.slice(i, i + cols)
      })
    }
  }
  return rows
}

/**
 * 计算带标题行的虚拟宫格全量卡片坐标，供框选覆盖视口外的未挂载卡片。
 */
export function rectsFromGroupedGridRows(
  rows: GroupedGridRow[],
  columnWidth: number,
  folderRowHeight: number,
  cardWidth: number,
  cardHeight: number
): ItemContentRect[] {
  const rects: ItemContentRect[] = []
  let top = 0
  for (const row of rows) {
    if (row.kind === 'header') {
      top += DIMENSION_GROUP_HEADER_HEIGHT
      continue
    }
    row.folders.forEach((folder, columnIndex) => {
      if (folder.virtual) return
      const left = columnIndex * columnWidth
      rects.push({
        id: folder.folderId,
        left,
        right: left + cardWidth,
        top,
        bottom: top + cardHeight
      })
    })
    top += folderRowHeight
  }
  return rects
}
